import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import clerk from '@/lib/clerk';
import ConfirmDialog from './ConfirmDialog.vue';
import VerifyDialog from '@/views/pages/login/VerifyDialog.vue';
import router from '@/router';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { commitAddNotification } from '@/store/main/mutations';
import { store } from '@/store/index';
import { readForceEnroll2Fa } from '@/store/main/getters';
import {
  dispatchFetchForceEnroll2Fa,
  dispatchLogOut,
} from '@/store/main/actionsMain';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'TwoFactorSettings',
  setup(__props) {

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  setupTwoFactorAuth: '二要素認証を設定する',
  securityEnhancement:
    'パスワードと認証コードの両方で確認を行うことで、セキュリティを強化します。',
  samlLoginCaution:
    '※SAMLを設定されたお客様につきましては、自動で二要素認証がOFFになります。',
  useSMS: 'SMSを使う',
  totpTwoFactorDescription:
    'Google AuthenticatorやMicrosoft Authenticatorなどの認証アプリを使って二要素認証を行います。',
  smsTwoFactorDescription: 'SMSで送信されるコードで二要素認証を行います。',
  registeredPhoneNumber: '登録済みの二要素認証用電話番号',
  changePhoneInstruction: '変更する場合は機能をオフにして再登録してください',
  smsAuth: 'SMS認証',
  recommended: '推奨',
  tfaUpdated: '二要素認証の設定を更新しました',
  authenticatorEnabledInfo:
    '認証アプリ（Google Authenticatorなど）に表示されるコードで認証を行います',
  useEmail: 'メールを使う',
  emailTwoFactorDescription:
    'ログイン中のメールアドレスに送信されるコードで二要素認証を行います。',
  emailEnabledInfo: '{email} に送信されるコードで認証を行います',
  emailBlockedByEnforcement:
    '管理者により二要素認証が必須化されているため、メール認証は選択できません。',
});

const {
  COMPANY_FEATURES,
  isFeatureActive,
  isIdpInitiatedEnabled,
  generateInactiveMessage,
} = useCompanyFeatureComposable();
const isConfirmDialogOpen = ref({});
const isTfaVerifyDialogOpen = ref(false);
const tfaVerifyConfirmResolver = ref<() => void | undefined>(undefined);
const availableMethodsForTfa = ref<string[]>([]);
const currentType = ref('');
const clerkUser = ref(clerk.user);
const mayUseSmsTfa = computed(() =>
  isFeatureActive(COMPANY_FEATURES.MANAGE_SMS_AUTHENTICATION)
);
const isSamlLoginEnabled = computed(() =>
  isFeatureActive(COMPANY_FEATURES.MANAGE_SAML_AUTHENTICATION)
);
// SAMLは通常ケミカン独自の二要素認証と排他だが、IdP-Initiated flow許可済みの
// テナントは例外的に併用を許可する（管理画面側の設計と対になる。SE-800）。
const blocksTfaDueToSaml = computed(
  () => isSamlLoginEnabled.value && !isIdpInitiatedEnabled.value
);
const mfaPhoneNumbers = computed(() =>
  clerkUser.value.phoneNumbers
    .filter((ph) => ph.verification.status === 'verified')
    .filter((ph) => ph.reservedForSecondFactor)
    .sort((ph) => (ph.reservedForSecondFactor ? -1 : 1))
);
const isSmsAuthEnabled = computed(() => mfaPhoneNumbers.value.length > 0);
const isTotpEnabled = computed(() => clerkUser.value.totpEnabled);
// Email 2FA (Clerk Client Trust). This is a per-user preference stored in Clerk
// `unsafeMetadata` (frontend-readable/writable). Client Trust itself is a global
// Clerk setting we can't gate per user, so this flag's only effect is to gate
// the logout client-destroy in actionLogOut: when on, the user is re-challenged
// by email on every login; when off, only on a new/untrusted device.
const isEmailAuthEnabled = ref(
  !!clerkUser.value?.unsafeMetadata?.email2faEnabled
);
const userEmail = computed(
  () => clerkUser.value?.primaryEmailAddress?.emailAddress || ''
);
// Gate the email 2FA option behind the 2FA_EMAIL_AUTHENTICATION
// company feature (per-client entitlement).
// 必須化中の未設定ユーザーは、メール認証では必須化の条件を満たせない（Clerkの
// two_factor_enabledがtrueにならない）ため、メール認証をONにできないようにする。
const isEmailBlockedByEnforcement = computed(
  () => readForceEnroll2Fa(store) && !isEmailAuthEnabled.value
);
const isEmailAuthGateEnabled = computed(() =>
  isFeatureActive(COMPANY_FEATURES.EMAIL_AUTHENTICATION_AS_2FA)
);

const formatInternationalPhoneNumber = (
  phoneNumber: string | undefined
): string => {
  if (!phoneNumber) return '';

  if (phoneNumber.startsWith('+81')) {
    return phoneNumber.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '$1-$2-$3-$4');
  }
  return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})(\d*)/, '$1-$2-$3-$4');
};

const resetToggle = (): void => {
  isConfirmDialogOpen.value = { ...isConfirmDialogOpen.value, isOpen: false };
};

const handleToggle = (type: 'totp' | 'phone_code' | 'email') => {
  currentType.value = type;

  if (type === 'phone_code') {
    isConfirmDialogOpen.value = {
      isOpen: true,
      forActivation: !isSmsAuthEnabled.value,
      mode: 'phone_code',
    };
  } else if (type === 'totp') {
    isConfirmDialogOpen.value = {
      isOpen: true,
      forActivation: !isTotpEnabled.value,
      mode: 'totp',
    };
  } else if (type === 'email') {
    isConfirmDialogOpen.value = {
      isOpen: true,
      forActivation: !isEmailAuthEnabled.value,
      mode: 'email',
    };
  }
};

const moveToNext = async (): Promise<void> => {
  isConfirmDialogOpen.value = { ...isConfirmDialogOpen.value, isOpen: false };
  router.push({ name: 'twoFactorSetup', params: { mode: currentType.value } });
};

const reverifyAuth = async (): Promise<void> => {
  // Prompt for reverification for TFA actions.
  // Clerk will refuse TFA actions if the verification is older than 10 minutes.
  // However there are other conditions that are impossible to check from the frontend
  // before making a request, so we resort to always requiring re-verification.
  await new Promise((resolve) => {
    isTfaVerifyDialogOpen.value = true;
    tfaVerifyConfirmResolver.value = resolve;
  });
};

const handleTfaVerifySuccess = async (): Promise<void> => {
  tfaVerifyConfirmResolver.value();
  tfaVerifyConfirmResolver.value = undefined;
  isTfaVerifyDialogOpen.value = false;
};

const handleConfirmation = async (): Promise<void> => {
  isConfirmDialogOpen.value = { ...isConfirmDialogOpen.value, isOpen: false };

  if (!['totp', 'phone_code', 'email'].includes(currentType.value)) {
    console.error(`Unexpected 2FA type: ${currentType.value}`);
    commitAddNotification(store, {
      content: $t(sharedMessages.genericError),
      color: 'error',
    });
    return;
  }

  await reverifyAuth();

  if (currentType.value === 'email') {
    // Password re-verify already ran above. Persist the preference in Clerk
    // unsafeMetadata; enabling also turns off any Clerk MFA (mutually exclusive
    // with Authenticator/SMS).
    try {
      if (isEmailAuthEnabled.value) {
        // Disable: clear the preference and stay logged in (matches the
        // Authenticator/SMS disable flow).
        await clerk.user.update({
          unsafeMetadata: {
            ...clerk.user.unsafeMetadata,
            email2faEnabled: false,
          },
        });
        isEmailAuthEnabled.value = false;
        clerkUser.value = clerk.user;
        commitAddNotification(store, {
          messageKey: m.tfaUpdated,
          color: 'success',
        });
      } else {
        // Enable: set the preference and turn off any Clerk MFA (mutually
        // exclusive with Authenticator/SMS).
        await clerk.user.update({
          unsafeMetadata: {
            ...clerk.user.unsafeMetadata,
            email2faEnabled: true,
          },
        });
        if (isTotpEnabled.value) {
          await clerk.user.disableTOTP();
        }
        const reservedPhone = clerk.user.phoneNumbers.find(
          (p) => p.reservedForSecondFactor
        );
        if (reservedPhone) {
          await reservedPhone.destroy();
        }
        isEmailAuthEnabled.value = true;
        // Force logout and re-login, consistent with the Authenticator/SMS
        // enable flow. On the next sign-in the (now untrusted) client triggers
        // the Client Trust email challenge.
        await dispatchLogOut(store, { tfaCompleted: true });
      }
    } catch (error) {
      console.log(error);
      commitAddNotification(store, {
        content: $t(sharedMessages.genericError),
        color: 'error',
      });
    }
    return;
  }

  // TOTP / SMS: move to the setup screen when not enabled yet, otherwise disable.
  const isEnabled =
    currentType.value === 'totp' ? isTotpEnabled.value : isSmsAuthEnabled.value;
  if (!isEnabled) {
    moveToNext();
    return;
  }

  try {
    if (currentType.value === 'totp') {
      await clerk.user.disableTOTP();
    } else {
      const tfaPhoneNumber = clerk.user.phoneNumbers.find(
        (a) => a.reservedForSecondFactor
      );
      if (!tfaPhoneNumber) {
        throw new Error(
          'User requested tfa phone number deletion but no phone number found in Clerk'
        );
      }
      await tfaPhoneNumber.destroy();
    }
    commitAddNotification(store, {
      messageKey: m.tfaUpdated,
      color: 'success',
    });
    // Reload user data
    clerkUser.value = clerk.user;
    // Removing a factor may leave the user without 2FA; refetch so the
    // enforcement guard applies immediately.
    await dispatchFetchForceEnroll2Fa(store);
  } catch (error) {
    console.log(error);
    commitAddNotification(store, {
      content: $t(sharedMessages.genericError),
      color: 'error',
    });
  }
};

return { __sfc: true,$t, m, COMPANY_FEATURES, isFeatureActive, isIdpInitiatedEnabled, generateInactiveMessage, isConfirmDialogOpen, isTfaVerifyDialogOpen, tfaVerifyConfirmResolver, availableMethodsForTfa, currentType, clerkUser, mayUseSmsTfa, isSamlLoginEnabled, blocksTfaDueToSaml, mfaPhoneNumbers, isSmsAuthEnabled, isTotpEnabled, isEmailAuthEnabled, userEmail, isEmailBlockedByEnforcement, isEmailAuthGateEnabled, formatInternationalPhoneNumber, resetToggle, handleToggle, moveToNext, reverifyAuth, handleTfaVerifySuccess, handleConfirmation, ConfirmDialog, VerifyDialog, sharedMessages }
}

})